import {
  Dialog, IconButton, Typography, Box, Chip, Button, useTheme, alpha,
} from '@mui/material';
import { LuX, LuSparkles, LuPlay, LuDot, LuExternalLink, LuBellOff } from 'react-icons/lu';
import AppIcon from '../../../components/icons/app-icon';
import { useNavigate } from 'react-router-dom';
import { ChangelogEntry } from '../configs/changelog';
import { useFeatureTour } from '../hooks/useFeatureTour';
import AnimatedReleaseBadge from './animated-release-badge';

export const WHATS_NEW_DISMISS_KEY = 'whatsNewDismissed';

interface WhatsNewModalProps {
  open: boolean;
  onClose: () => void;
  entry: ChangelogEntry;
  hasRouteAccess?: boolean;
}

const WhatsNewModal = ({ open, onClose, entry, hasRouteAccess = true }: WhatsNewModalProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { startTour } = useFeatureTour();

  const canTakeTour = entry.tourSteps && entry.tourSteps.length > 0 && hasRouteAccess;

  const handleViewAll = () => {
    onClose();
    navigate('/whats-new');
  };

  const handleDontShowAgain = () => {
    localStorage.setItem(WHATS_NEW_DISMISS_KEY, 'true');
    onClose();
  };

  const handleTour = () => {
    onClose();
    if (entry.tourSteps) {
      startTour(entry.tourSteps, entry.tourRoute);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden', bgcolor: 'background.paper' } }}
    >
      {/* Brand header */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          px: 3, py: 2.5, position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute', top: 8, right: 8,
            color: alpha(theme.palette.primary.contrastText, 0.7),
            '&:hover': {
              color: theme.palette.primary.contrastText,
              bgcolor: alpha(theme.palette.primary.contrastText, 0.15),
            },
          }}
        >
          <AppIcon icon={LuX} fontSize="small" />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AppIcon icon={LuSparkles} size={28} color={theme.palette.primary.contrastText} />
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'primary.contrastText' }}>
              What's New
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
              <Chip
                label={`v${entry.version}`}
                size="small"
                sx={{
                  height: 20, fontSize: '10px', fontWeight: 700, fontFamily: 'monospace',
                  bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
                  color: 'primary.contrastText',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Typography sx={{ fontSize: '11px', color: alpha(theme.palette.primary.contrastText, 0.7) }}>
                {new Date(entry.date).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric',
                })}
              </Typography>
              {entry.badge && (
                <AnimatedReleaseBadge label={entry.badge} onPrimary />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
          {entry.sparkles && (
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '8px',
                bgcolor: alpha(theme.palette.primary.main, 0.14),
              }}
            >
              <AppIcon icon={LuSparkles} size={17} color='primary.main' />
            </Box>
          )}
          <Typography
            sx={{
              fontSize: entry.sparkles ? '17px' : '15px',
              fontWeight: entry.sparkles ? 800 : 700,
              color: 'text.primary',
            }}
          >
            {entry.title}
          </Typography>
        </Box>

        {entry.description && (
          <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.55, mb: 1.5 }}>
            {entry.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2.5 }}>
          {entry.highlights.map((h, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <AppIcon
                icon={LuDot}
                size={20}
                color={theme.palette.primary.main}
                sx={{ mt: '1px' }}
              />
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5 }}>
                {h}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Quick links */}
        {entry.links && entry.links.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {entry.links.map((link) => (
              <Button
                key={link.url}
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<AppIcon icon={LuExternalLink} size={14} />}
                onClick={() => { onClose(); navigate(link.url); }}
                sx={{
                  fontSize: '12px', fontWeight: 500,
                  borderRadius: '8px', borderColor: 'divider', color: 'text.secondary',
                  '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {canTakeTour && (
            <Button
              variant="contained"
              startIcon={<AppIcon icon={LuPlay} fontSize="small" />}
              onClick={handleTour}
              sx={{
                flex: 1, fontSize: '13px', fontWeight: 600,
                borderRadius: '8px',
                height: 38,
              }}
            >
              Take a tour
            </Button>
          )}
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleViewAll}
            sx={{
              flex: 1, fontSize: '13px', fontWeight: 600,
              borderRadius: '8px', borderColor: 'divider', color: 'text.secondary',
              '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
              height: 38,
            }}
          >
            View all updates
          </Button>
        </Box>

        {/* Don't show again */}
        <Box
          onClick={handleDontShowAgain}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 0.5, mt: 2, pt: 1.5, cursor: 'pointer',
            color: 'text.secondary', '&:hover': { color: 'text.primary' },
            transition: 'color 0.15s',
          }}
        >
          <AppIcon icon={LuBellOff} size={14} />
          <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
            Don't show this again
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default WhatsNewModal;
